import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import CustomBreadcrumbs from "../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useLocation, useNavigate } from "react-router-dom";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { PreferencesContext } from "../../PreferenceContext";
import {
  CreateSpecialMedicalCondition,
  GetSpecialMedicalConditionDetail,
  UpdateSpecialMedicalCondition,
} from "../../services/SpecialMedicalConditionService";
import { EncryptDecryptService } from "../../services/common/EncryptionService";
import { AlertType } from "../../services/models/AlertType";
import { LoadingButton } from "@mui/lab";
import SkeletonWrapper from "../../components/Skeleton/SkeletonWrapper";
import { CustomSelect } from "../../components/CustomFields/CustomSelect";
import { PAGE_SIZE } from "../../services/common/const";
import { GetSpecialityList } from "../../services/SpecialityService";
import { GetDepartmentList } from "../../services/DepartmentService";

// SCHEMA FOR OPTION
interface selectOption {
  label: string;
  value: string;
}

// SCHEMA FOR FORM
interface formInitialValueTypes {
  specialMedicalConditionId: number;
  medicalConditionName: string;
  departmentId: selectOption | null;
  specialityId: selectOption | null;
  isDeleted: boolean;
}

// SCHEMA FOR PARAMETER
interface Parameter {
  specialMedicalConditionId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  tabType: number;
}
// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  specialMedicalConditionId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  tabType: 0,
};

const SpecialMedicalConditionMasterSave = (props: any) => {
  // PROPS
  const { isForPopUp = false, handleClosePopUp, name } = props;

  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [departmentData, setdepartmentData] = useState<any[]>([]);
  const [specialityData, setspecialityData] = useState<any[]>([]);
  const [param, setParam] = useState(parameters);

  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTTING PARAMS
  let urlParams = useQuery();
  let curRawSpecialMedicalConditionId = urlParams.get("q");
  let curSpecialMedicalConditionId = 0;
  if (
    curRawSpecialMedicalConditionId !== null &&
    curRawSpecialMedicalConditionId !== undefined &&
    !isForPopUp
  ) {
    let decId = edService.decrypt(curRawSpecialMedicalConditionId);
    if (decId !== "" && decId !== null && decId !== undefined) {
      curSpecialMedicalConditionId = +decId;
    }
  }

  // DEFAULT SCHEMA VALUE FOR FORM
  const initSpecialMedicalConditionMasterData: formInitialValueTypes = {
    specialMedicalConditionId: 0,
    medicalConditionName: isForPopUp ? name?.toUpperCase() : "",
    isDeleted: false,
    departmentId:
      userContext?.departmentId > 0
        ? {
            label: userContext?.departmentName || "",
            value: userContext?.departmentId?.toString(),
          }
        : null,
    specialityId:
      userContext?.specialityId > 0
        ? {
            label: userContext?.specialityName || "",
            value: userContext?.specialityId?.toString(),
          }
        : null,
  };

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    specialMedicalConditionId: Yup.number().nullable(),
    medicalConditionName: Yup.string().required(
      "Medical Condition Name is required",
    ),
    departmentId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Department is required"),
    specialityId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Speciality is required"),
  });

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
    defaultValues: initSpecialMedicalConditionMasterData, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
  });

  // GET DETAIL
  const getSpecialMedicalConditionDetailById = async () => {
    let specialMedicalConditionMaster: any;
    let params = new URLSearchParams();
    params.append(
      "specialMedicalConditionId",
      curSpecialMedicalConditionId.toString(),
    );
    params.append("consumerId", userContext.consumerId.toString());
    params.append("orgId", userContext.organizationId.toString());
    params.append("branchId", userContext.branchId.toString());

    try {
      const res: any = await GetSpecialMedicalConditionDetail(params);
      specialMedicalConditionMaster = res.data.data;
      methods.reset({
        specialMedicalConditionId:
          specialMedicalConditionMaster?.specialMedicalConditionId || 0,
        medicalConditionName:
          specialMedicalConditionMaster?.medicalConditionName || "",
        departmentId: specialMedicalConditionMaster?.departmentId
          ? {
              value: specialMedicalConditionMaster?.departmentId?.toString(),
              label: specialMedicalConditionMaster?.departmentName || "",
            }
          : null,
        specialityId: specialMedicalConditionMaster?.specialityId
          ? {
              label: specialMedicalConditionMaster?.specialityName || "",
              value:
                specialMedicalConditionMaster?.specialityId?.toString() || "",
            }
          : null,
      });
      return specialMedicalConditionMaster;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // SAVE AND UPDATE SPECIAL MEDICAL CONDITION
  const saveSpecialMedicalConditionMaster = async (
    value: formInitialValueTypes,
  ) => {
    try {
      let result: any = "";
      let formData = {
        specialMedicalConditionId: value.specialMedicalConditionId,
        consumerId: userContext.consumerId.toString(),
        orgId: userContext.organizationId.toString(),
        branchId: userContext.branchId.toString(),
        medicalConditionName: value?.medicalConditionName.trim() || "",
        departmentId: value?.departmentId?.value || null,
        specialityId: value?.specialityId?.value || null,
        dataManagedBy: userContext.loginDetail.userId,
      };
      if (curSpecialMedicalConditionId > 0) {
        result = await UpdateSpecialMedicalCondition(formData);
      } else {
        result = await CreateSpecialMedicalCondition(formData);
      }
      if (result) {
        if (!isForPopUp) {
          navigate(-1);
        } else {
          handleClosePopUp(
            curSpecialMedicalConditionId > 0
              ? "MEDICAL_CONDITION_UPDATE"
              : "MEDICAL_CONDITION_CREATE",
            result?.data,
          );
        }
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // GET DEPARTMENT
  async function fetchDepartment() {
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext?.consumerId?.toString());
      params.append("orgId", userContext?.organizationId?.toString());
      params.append("branchId", userContext?.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      params.append("mode", "DROPDOWN".toString());
      const departmentData: any = await GetDepartmentList(params);
      if (departmentData?.data?.data?.departmentColl) {
        let departmentCollection = departmentData.data.data.departmentColl;
        setdepartmentData(departmentCollection);
      }
      // setIsLoading(false);
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // GET SPECIALITY
  async function fetchSpeciality(departmentIds: string) {
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("departmentIds", departmentIds);
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      params.append("mode", "DROPDOWN".toString());
      const specialityData: any = await GetSpecialityList(params);
      if (specialityData?.data?.data?.specialityColl) {
        let specialityCollection = specialityData.data.data.specialityColl;
        setspecialityData(specialityCollection);
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // ON SUBMIT OF FORM
  const onSubmit = async (data: any) => {
    saveSpecialMedicalConditionMaster(data);
  };

  // HANDLE BACK
  const handleBack = () => {
    if (!isForPopUp) {
      navigate(-1);
    } else {
      handleClosePopUp("CLOSE");
    }
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      if (curSpecialMedicalConditionId <= 0) {
        await fetchDepartment();
      }
      if (curSpecialMedicalConditionId > 0) {
        await getSpecialMedicalConditionDetailById();
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [curSpecialMedicalConditionId]);

  return (
    <Box>
      <Box className={"main-container"}>
        {isForPopUp === false && (
          <>
            <Grid container className="page-header">
              <Grid item xs={6} sm={6} md={6}>
                <Box
                  sx={{
                    display: "flex",
                    height: "100%",
                    alignItems: "center",
                  }}>
                  <CustomBreadcrumbs />
                </Box>
              </Grid>
              <Grid item xs={6} sm={6} md={6}>
                <Box className="page-header-icon">
                  <Button
                    color="primary"
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    onClick={handleBack}
                    startIcon={<ArrowBackIcon />}>
                    Back
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </>
        )}
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} action="">
            <Grid container gap={2}>
              <Grid item xs={12} mb={3} className="group-container">
                <Grid item container xs={12} spacing={2}>
                  <Grid item xs={12} sm={6} md={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.departmentId
                            ? "validation-message"
                            : ""
                        }`}>
                        Department *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input">
                        <Controller
                          control={methods.control}
                          name="departmentId"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"departmentId"}
                              FieldName={"departmentId"}
                              options={departmentData}
                              isDisabled={curSpecialMedicalConditionId > 0}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e: any) => {
                                if (
                                  methods
                                    ?.getValues()
                                    ?.departmentId?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "departmentId",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                  methods.setValue("specialityId", null, {
                                    shouldValidate: true,
                                  });

                                  if (e?.value) {
                                    setIsLoading(true);
                                    await fetchSpeciality(e.value);
                                    setIsLoading(false);
                                  } else {
                                    setspecialityData([]);
                                  }
                                }
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.departmentId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.specialityId
                            ? "validation-message"
                            : ""
                        }`}>
                        Speciality *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input">
                        <Controller
                          control={methods.control}
                          name="specialityId"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"specialityId"}
                              FieldName={"specialityId"}
                              options={specialityData}
                              isDisabled={curSpecialMedicalConditionId > 0}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e: any) => {
                                if (
                                  methods
                                    ?.getValues()
                                    ?.specialityId?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "specialityId",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                }
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.specialityId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.medicalConditionName
                            ? "validation-message"
                            : ""
                        }`}>
                        Medical Condition Name *
                      </FormLabel>
                      <TextField
                        placeholder="Enter Medical Condition Name"
                        type="text"
                        fullWidth
                        size="small"
                        autoFocus
                        {...methods.register("medicalConditionName", {
                          required: true,
                        })}
                        onChange={(e) => {
                          methods.setValue(
                            "medicalConditionName",
                            e.target.value.toUpperCase(),
                          );
                        }}
                        error={!!methods.formState.errors.medicalConditionName}
                        className={`text-input ${
                          methods.formState.errors.medicalConditionName
                            ? "error"
                            : ""
                        }`}
                      />
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.medicalConditionName?.message}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="contained"
                    type="submit"
                    className="primary-button"
                    name="save"
                    disabled={isLoading || methods.formState.isSubmitting}>
                    {curSpecialMedicalConditionId > 0 ? "Update" : "Save"}
                  </LoadingButton>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    name="cancel"
                    disabled={isLoading || methods.formState.isSubmitting}
                    onClick={handleBack}>
                    Cancel
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </Box>
    </Box>
  );
};

export default SpecialMedicalConditionMasterSave;
